<script setup>

import StickyGrid from "@/components/common/other/StickyGrid";
import Button from "@/components/common/input/Button";
import StaticWidget from "@/components/common/other/StaticWidget";
import StaticTooltip from "@/components/common/other/StaticTooltip";
import BusySpinner from "@/components/common/other/BusySpinner";
import CheckBox from "@/components/common/input/CheckBox.vue";
import BrowsePicksSearchPillGroup from "@/components/warehouse/BrowsePicksSearchPillGroup.vue";
import API from "@/classes/global/API";

import {inject, ref, onMounted} from 'vue';
import {useStore} from 'vuex';
import {useDownloadLocalFile} from "@/composables/common/useDownloadLocalFile";
import {useCheckSearchCriteriaExists} from "@/composables/common/useCheckSearchCriteriaExists";

const store = useStore();

const browsePicks = inject('browsePicks');

onMounted(() => {

  if (useCheckSearchCriteriaExists(browsePicks.value.searchCriteria)) return handleSearch();

  prepareMetaData();

})

async function prepareMetaData() {

  const response = await API.getPickstockShipments();

  if(response) browsePicks.value.searchData = response;

  const metaData = await API.getStockMetaData('pickStockValidationFields' );

  browsePicks.value.skuGroups = metaData.skuGroups;

}

function handleClearAllFields() {

  browsePicks.value.isSearchData = false;

  prepareMetaData();

}

function handleRefresh() {

  if (browsePicks.value.isSearchData) {

    return handleSearch();

  }

  prepareMetaData();
}

async function handleSearch() {

  const result = await API.searchPickstockShipments(browsePicks.value.searchCriteria);

  browsePicks.value.searchData = result

}

function toggleSearchGroup(button) {

  if(button && browsePicks.value.searchPillGroupTwo) return;
  if(!button && !browsePicks.value.searchPillGroupTwo) return;

  browsePicks.value.searchPillGroupTwo = !browsePicks.value.searchPillGroupTwo;
}

async function downloadToExcel() {

  browsePicks.value.hideExcelDownloadBusy = false;

  const response = await API.browsePicksExcelDownload(browsePicks.value.searchCriteria,
      useCheckSearchCriteriaExists(browsePicks.value.searchCriteria));

  if(response) {

    try {

      useDownloadLocalFile(response);

    } catch(error) {

      browsePicks.value.hideExcelDownloadBusy = true;
      store.state.banners.error = new Notification(true, 'Could Not Process The File');
    }

  }

  browsePicks.value.hideExcelDownloadBusy = true;
}

async function getWarehouseDocs(warehouseRef) {

  // TODO remove when suitable testing is applied for documents
  // browsePicks.value.openDocumentsDrawer = true;
  // browsePicks.value.shipmentDocs = [
  //
  //   {
  //     Id: 1,
  //     name: 'Some Doc',
  //     fileName: 'this is the filename',
  //     description: 'Doc description',
  //     quoteNumber: 'Quote number',
  //     invoiceNumber: 'Invoice number'
  //   }
  // ]

  const result = await API.getWarehouseDocumentsList(warehouseRef);

  if(result) {

    browsePicks.value.shipmentDocs = result;
    browsePicks.value.openDocumentsDrawer = true;
  }
}

function viewSelectedShipment(row) {

  store.state.hideLoadingSpinner = false;

  try {

    router.push({ name: 'viewshipment', params: { owner: store.state.owner, mode: row.mode, id: row.shipmentRef }})

  } catch (error) {

    store.state.hideLoadingSpinner = true;
  }

}

</script>

<template class="cmp-browse-picks">
  <div class="cmp-browse-picks_wrapper">
    <StaticWidget
        title="Search & View Jobs"
        height="735">
      <template #extra-content>
        <div class="cmp-browse-picks_search-group-selection">
          <span>Search Group</span>
          <Button
              @click="toggleSearchGroup(false)"
              type="toolbar"
              text="1"
              :class="{'cmp-browse-picks_search-group-selection-icon-active' : !browsePicks.searchPillGroupTwo}"
              style="width: 30px;">
          </Button>
          <Button
              @click="toggleSearchGroup(true)"
              type="toolbar"
              text="2"
              :class="{'cmp-browse-picks_search-group-selection-icon-active' : browsePicks.searchPillGroupTwo}"
              style="width: 30px;">
          </Button>
        </div>
        <div style="margin-left: 100px; color: #6e6e6e">Filter:</div>
        <div style="margin: 3px 0 0 10px;">
          <CheckBox
              v-model="browsePicks.searchCriteria.completed"
              @change="handleSearch()"
              :label-width="56"
              label="Completed">
          </CheckBox>
        </div>
        <div style="margin: 3px 0 0 50px;">
          <CheckBox
              v-model="browsePicks.searchCriteria.notification"
              @change="handleSearch()"
              :label-width="112"
              label="Notification Not Sent">
          </CheckBox>
        </div>
      </template>
      <BrowsePicksSearchPillGroup
          @clear="handleClearAllFields"
          v-model="browsePicks">
      </BrowsePicksSearchPillGroup>
      <StaticWidget title="Search Results" height="580">
        <template #extra-content>
          <div style="font-weight: normal; font-size: 12px; color: var(--aqua-blue-definition); margin-left: 20px;">
            Click on rows to view each line information
          </div>
          <Button
              @click="handleRefresh()"
              icon="<i class='fa-light fa-arrows-rotate'></i>"
              text="Refresh"
              type="secondary">
          </Button>
        </template>
        <template #right-side>
          <BusySpinner :hide="browsePicks.hideExcelDownloadBusy" message="Downloading Data..."></BusySpinner>
          <StaticTooltip :icon="false" v-if="browsePicks.hideExcelDownloadBusy">
            <template #content>
              This will download a full stock list to an excel file.
              Any filters or searches are not included within
              the created file.
            </template>
            <i class="fa-solid fa-circle-info icon-info"></i>
          </StaticTooltip>
          <Button
              v-if="browsePicks.hideExcelDownloadBusy"
              @click="downloadToExcel()"
              icon='<i class="fa-solid fa-file-excel"></i>'
              text="Export"
              type="success">
          </Button>
        </template>
        <div class="cmp-browse-picks_search-results-wrapper">
          <StickyGrid
              :allowGridMenu="false"
              :height="540"
              :page-size="100"
              :selectable="false"
              :highlightRows="false"
              :showDateIndicator="false"
              :show-tips="false"
              :row-selection="false"
              v-model:currentPage="browsePicks.searchGridCurrentPage"
              v-model:currentPaginationRange="browsePicks.searchGridPageRange"
              :dataSource="browsePicks.searchData"
              :columns="browsePicks.gridColumns">
            <template v-slot:documents="row">
              <i v-if="row.grid.documents"
                 title="View Documents"
                 @click="getWarehouseDocs(row.grid.warehouseRef)"
                 class="fa-solid fa-folder-open grid-docs-icon">
              </i>
            </template>
            <template v-slot:complete="row">
              <CheckBox
                  :disabled="true"
                  style="margin-left: 20px"
                  v-model="row.grid.complete">
              </CheckBox>
            </template>
            <template v-slot:notified="row">
              <CheckBox
                  :disabled="true"
                  style="margin-left: 24px"
                  v-model="row.grid.notified">
              </CheckBox>
            </template>
            <template v-slot:shipmentRef="row">
              <div
                  @click="viewSelectedShipment(row.grid)"
                  title="View Job Data"
                  class="cmp-browse-picks_grid-link"><u>{{row.rowData.shipmentRef}}</u>
              </div>
            </template>
            <template v-slot:warehouseRef="row">
              <div
                  v-if="!row.rowData.shipmentRef"
                  @click="viewSelectedShipment(row.grid.id)"
                  title="Edit Warehouse Pick Detail"
                  class="cmp-browse-picks_grid-link"><u>{{row.rowData.warehouseRef}}</u>
              </div>
              <div :title="row.rowData.warehouseRef" v-else>{{row.rowData.warehouseRef}}</div>
            </template>
          </StickyGrid>
        </div>
      </StaticWidget>
    </StaticWidget>
  </div>

</template>

<style lang="scss">

.cmp-browse-picks {

  &_search-group-selection {
    display: flex;
    gap: 5px;
    align-items: center;
    margin-left: 60px;
    font-weight: normal;
    font-size: 12px;
  }

  &_search-group-selection-icon {
    font-size: 24px;
    color: #a94e0e;
  }

  &_search-group-selection-icon-active {
    background-image: none !important;
    background-color: white;
    font-weight: bold;
    color: black !important;
    box-shadow: 1px 1px 2px 0px #484848 !important;
  }

  &_grid-link {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    color: var(--aqua-blue-definition);
    cursor: pointer;
  }

}
</style>
